import { CheckIcon, ClipboardCopyIcon } from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import { FC, useEffect, useRef, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';

interface Props {
  /** Prism language id; empty leaves the header's label out. */
  language: string;
  value: string;
}

const MONO =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, "Liberation Mono", monospace';

// The panel is dark in both themes, so the block needs no theme state and
// follows a theme switch without remounting. Prism's own background, margin
// and text shadow are dropped; the panel draws the surface.
const PRE_STYLE = {
  margin: 0,
  padding: '12px 16px 14px',
  background: 'transparent',
  borderRadius: 0,
  fontSize: '13px',
  lineHeight: '22px',
  fontFamily: MONO,
  textShadow: 'none',
  overflowX: 'auto',
} as const;

const CODE_PROPS = {
  style: {
    fontFamily: MONO,
    fontSize: 'inherit',
    lineHeight: 'inherit',
    textShadow: 'none',
    background: 'transparent',
    whiteSpace: 'pre',
  },
} as const;

/**
 * A fenced code block: a dark panel with the language and a Copy button in
 * its own header row. Code keeps its indentation and scrolls sideways inside
 * the panel instead of wrapping (`.code-block` in globals.css undoes the
 * global `pre code` wrap rule). Used by every Markdown surface, not only the
 * chat, so it carries no chat-only classes.
 */
export const CodeBlock: FC<Props> = ({ language, value }) => {
  const { t } = useLocale('common');
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className='code-block my-4 overflow-hidden rounded-xl bg-[#0f1020] text-start font-sans ring-1 ring-[#0f0f28]/[0.08] dark:ring-white/10'
      dir='ltr'
    >
      <div className='flex h-9 items-center justify-between gap-3 border-b border-white/[0.08] pe-1.5 ps-4 [@media(pointer:coarse)]:h-11'>
        <span className='min-w-0 truncate text-[12px] font-medium lowercase leading-none text-slate-400'>
          {language}
        </span>
        <button
          type='button'
          onClick={copyToClipboard}
          className='relative inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2 text-[12px] font-medium leading-none text-slate-400 transition-colors before:absolute before:-inset-1 hover:bg-white/[0.08] hover:text-white focus-visible:[outline-style:solid] focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-primary-400 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:before:hidden'
        >
          {copied ? (
            <CheckIcon className='h-3.5 w-3.5' aria-hidden='true' />
          ) : (
            <ClipboardCopyIcon className='h-3.5 w-3.5' aria-hidden='true' />
          )}
          <span aria-live='polite'>{copied ? t('Copied') : t('Copy')}</span>
        </button>
      </div>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneDark}
        customStyle={PRE_STYLE}
        codeTagProps={CODE_PROPS}
      >
        {value}
      </SyntaxHighlighter>
    </div>
  );
};
